import {
  Form,
  FormSelect,
  FormTagsInput,
  FormTextInput,
  StandardFormButtons,
} from '@components/form';
import { Group } from '@components/layout';
import { useFileUpload } from '@fileUpload/FileUpload';
import {
  useCompleteSkillUploadMutation,
  useCreateSkillMutation,
  useGetWorkspacesNameListQuery,
} from '@generated/graphql';
import type { FileWithPath } from '@helper-functions/fileSystemTraversal';
import { zipFiles } from '@helper-functions/zipFiles';
import { MenuItem, Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { useNotifications } from '@notifications/Notifications';
import type { FormikHelpers } from 'formik';
import posthog from 'posthog-js';
import { useState } from 'react';
import * as yup from 'yup';
import { SkillFileField, type UploadState } from './SkillFileField';

const validationSchema = yup.object({
  scopeType: yup
    .string()
    .required('Scope type is required')
    .oneOf(['organization', 'user', 'workspace']),
  workspaceId: yup.string().when('scope', {
    is: 'workspace',
    then: (schema) => schema.required('Workspace id is required'),
  }),
  name: yup
    .string()
    .required('Name is required')
    .max(64, 'Name must be 64 characters or less')
    .matches(/^[a-z0-9-]+$/, 'Name can only contain lowercase letters, numbers, and hyphens'),
  description: yup.string(),
  version: yup.string(),
  tags: yup.array().of(yup.string()),
});

type SkillCreateModalFormValues = {
  scopeType: 'organization' | 'user' | 'workspace';
  workspaceId: string;
  name: string;
  description: string;
  version: string;
  tags: string[];
  file: File | FileWithPath[] | null;
  fileIsZip: boolean;
};

type SkillCreateModalProps = {
  fixedScopeType?: 'organization' | 'user' | 'workspace';
  fixedWorkspaceId?: string;
  onClose: () => void;
  onCreate: VoidFunction;
};

export const SkillCreateModal = ({
  fixedScopeType,
  fixedWorkspaceId,
  onClose,
  onCreate,
}: SkillCreateModalProps) => {
  const { organizationId } = useOrganizationContext();
  const { createAsyncNotification } = useNotifications();
  const [formError, setFormError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);

  const { data: workspacesData } = useGetWorkspacesNameListQuery({
    variables: { organizationId },
  });
  const workspaces = workspacesData?.getWorkspaces || [];

  const [createSkill] = useCreateSkillMutation();
  const [completeSkillUpload] = useCompleteSkillUploadMutation();
  const uploadFile = useFileUpload();

  const handleSubmit = createAsyncNotification(
    'Skill created successfully.',
    async (
      {
        scopeType,
        workspaceId,
        name,
        description,
        version,
        tags,
        file,
      }: SkillCreateModalFormValues,
      helpers: FormikHelpers<SkillCreateModalFormValues>,
    ) => {
      helpers.setSubmitting(true);

      try {
        let fileToUpload: File | Blob;

        // If folder selected, zip it client-side first
        if (Array.isArray(file)) {
          setUploadState('zipping');
          fileToUpload = await zipFiles(file);
        } else {
          fileToUpload = file;
        }

        // Step 1: Call createSkill to get presigned S3 URLs
        setUploadState('uploading');
        await createSkill({
          variables: {
            scopeType,
            organizationId: scopeType === 'organization' ? organizationId : undefined,
            workspaceId: scopeType === 'workspace' ? workspaceId || undefined : undefined,
            name: name.trim(),
            description: description.trim() || undefined,
            version: version.trim() || undefined,
            config: tags.length > 0 ? JSON.stringify({ tags }) : undefined,
            size: fileToUpload.size,
          },
          onCompleted: async (data) => {
            if (!data?.createSkill) {
              throw new Error('Failed to create skill — no upload URLs returned');
            }
            posthog.capture('workspace:user_create-workspace-skill', {
              organizationId,
              workspaceId,
              skillId: data.createSkill.skillId,
            });
            const { skillId, uploadId, partSize, urls } = data.createSkill;

            // Step 2: Upload file to S3 via presigned URLs (uses FileUpload context)
            // This handles multipart chunking, progress, retries (3 attempts), concurrency (3 parallel)
            const etags = await uploadFile({
              file: fileToUpload as File,
              urls,
              partSize,
              onUploadProgress: setUploadProgress,
            });

            // Step 3: Finalize multipart upload (no-op for single-part)
            setUploadState('completing');
            await completeSkillUpload({
              variables: {
                skillId,
                uploadId,
                parts: etags,
              },
              onCompleted: () => {
                posthog.capture('workspace:user_complete-workspace-skill-upload', {
                  skillId,
                });
                setUploadState('done');
                onCreate?.();
                onClose?.();
              },
              onError: (error) => {
                setFormError(error.message);
                throw error;
              },
            });
          },
          onError: (error) => {
            setFormError(error.message);
            throw error;
          },
        });
      } catch (err: unknown) {
        setUploadState('error');
        setFormError((err as Error)?.message || 'An error occurred during upload');
      } finally {
        helpers.setSubmitting(false);
      }
    },
    {
      loadingMessage: 'Creating skill...',
    },
  );

  return (
    <Form<SkillCreateModalFormValues>
      initialValues={{
        scopeType: fixedScopeType || 'organization',
        workspaceId: fixedWorkspaceId || '',
        name: '',
        description: '',
        version: '',
        tags: [],
        file: null,
        fileIsZip: false,
      }}
      validationSchema={validationSchema}
      validateOnBlur
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        return (
          <Stack>
            <FormSelect name="scopeType" label="Scope" disabled={!!fixedScopeType}>
              <MenuItem value="organization">Organization</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="workspace">Workspace</MenuItem>
            </FormSelect>
            {values.scopeType === 'workspace' && (
              <FormSelect name="workspaceId" label="Workspace" disabled={!!fixedWorkspaceId}>
                {workspaces.map((workspace) => (
                  <MenuItem key={workspace.workspaceId} value={workspace.workspaceId}>
                    {workspace.name}
                  </MenuItem>
                ))}
              </FormSelect>
            )}
            <FormTextInput
              name="name"
              label="Name"
              helperText="Lowercase letters, numbers, and hyphens only (max 64 characters)"
              required
            />
            <FormTextInput
              name="description"
              label="Description (optional)"
              placeholder="Brief description of what this skill does"
              multiline
              rows={3}
            />
            <Group gap={2} justifyContent="space-between" alignItems="flex-start">
              <FormTextInput name="version" label="Version (optional)" placeholder="1.0.0" />
              <FormTagsInput name="tags" />
            </Group>
            <SkillFileField name="file" uploadState={uploadState} uploadProgress={uploadProgress} />
            <StandardFormButtons
              onClose={onClose}
              formError={formError}
              submitLabel="Create skill"
            />
          </Stack>
        );
      }}
    </Form>
  );
};
